import * as Comlink from "comlink";

// Inlined (`?worker&inline`) so the worker is embedded in the main bundle as a
// blob — no separate chunk fetch at spawn time. A separate chunk can't be
// fetched when offline (there's no service worker), which left the worker
// unspawnable and `readBlob` hanging forever on offline image reads.
import LocalBlobStoreWorker from "./local-blob-store.worker.ts?worker&inline";
import type { OpfsBlobStore } from "./opfs-blob-store";
import type { AttachmentId, BlobEntry, LocalBlobStoreApi } from "./types";

type RemoteStore = Comlink.Remote<OpfsBlobStore>;

let cachedRemote: RemoteStore | null = null;

/**
 * Lazily spawn the LBS Worker and wrap it as a comlink proxy. Created on first
 * use so importing this module is side-effect-free.
 */
function getRemote(): RemoteStore {
  if (cachedRemote === null) {
    const worker = new LocalBlobStoreWorker({ name: "local-blob-store" });
    cachedRemote = Comlink.wrap<OpfsBlobStore>(worker);
  }
  return cachedRemote;
}

/**
 * Local Blob Store (LBS) — per-device, never-synced OPFS byte cache keyed by
 * `attachmentId`, backed by the LBS Worker. Bytes are moved across the boundary
 * as transferable `ArrayBuffer`s (zero-copy): the buffer passed to `writeBlob`
 * is detached on this side after the call.
 *
 * M0.1 is the headless byte store + its debug overlay panel; the Dexie index,
 * eviction, and object-URL management land in later chunks.
 */
export const localBlobStoreModule: LocalBlobStoreApi = {
  writeBlob(id: AttachmentId, bytes: ArrayBuffer): Promise<void> {
    return getRemote().writeBlob(id, Comlink.transfer(bytes, [bytes]));
  },
  readBlob(id: AttachmentId): Promise<ArrayBuffer | null> {
    return getRemote().readBlob(id);
  },
  deleteBlob(id: AttachmentId): Promise<void> {
    return getRemote().deleteBlob(id);
  },
  deleteAllBlobs(): Promise<void> {
    return getRemote().deleteAllBlobs();
  },
  exists(id: AttachmentId): Promise<boolean> {
    return getRemote().exists(id);
  },
  listBlobs(): Promise<BlobEntry[]> {
    return getRemote().listBlobs();
  },
};
